import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { CrmAccount, CrmClient, CrmClientAddress, CrmClientCommunication, CrmClientContactDirectoryEntry, CrmClientContactResolveRequest, CrmClientCreditSnapshot, CrmClientDocument, CrmLead, CrmProspect, CrmProspectConversionResult, CrmQuotation, CrmNote, CrmCommunicationLog, CrmReminder, CrmAnalyticsSummary, CrmActivityItem, PageResponse, CrmDuplicateResponse, CrmLeadConversionRequest, CrmLeadConversionResult, CrmClientEmployeeAssignment, CrmClientEmployeeAssignmentRequest, CrmClientRegistrationAttachment, CrmQuotationAttachment, CrmQuotationRequirement, CrmQuotationTask, CrmQuotationReviewNote, CrmQuotationExternalReview, CrmQuotationTermsTemplate, CrmQuotationClientCriteria, CrmScorecards, CrmMerchandiser, CrmRoleOption } from './crm.model';
import { APIResponseWrapper } from '../models/common/api-response-wrapper';
import { CrmClientQuickCreateRequest, CrmClientQuickContactRequest } from './crm.model';
import { PosmProject } from '../posm/models/posm-project.model';
import { ToolbarService } from '../services/toolbar.service';
import { SessionStorageService } from '../services/session-storage.service';
import { QuotationContractLink, QuoteToContractPreview, QuoteToContractRequest, QuoteToContractResult } from './quotations/quotation-contract-conversion.model';

export interface TransporterFleetOption {
  crmClientId: number;
  crmClientNumber?: string;
  transporterName: string;
  ownerCompanyId: number;
  linkedCompanyId: number;
  linkedCompanyName?: string;
  transportationAgencyId: number;
  transportationAgencyName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CrmService {
  private http = inject(HttpClient);
  private toolbarService = inject(ToolbarService);
  private sessionStorageService = inject(SessionStorageService);
  private apiUrl = `${environment.restApiUrl}crm`;

  private getCompanyId(): number {
    const company = this.sessionStorageService.getCompany();
    return company?.id || this.toolbarService.customer()?.company?.id || 0;
  }

  getCrmRoles(): Observable<CrmRoleOption[]> {
    return this.http.get<CrmRoleOption[]>(`${this.apiUrl}/roles`).pipe(
      map(roles => (roles || [])
        .map(role => ({
          ...role,
          value: String(role?.value ?? role?.code ?? '').trim(),
          label: String(role?.label ?? role?.value ?? role?.code ?? '').trim()
        }))
        .filter(role => !!role.value))
    );
  }

  getScopedTransporters(companyId?: number): Observable<TransporterFleetOption[]> {
    const id = companyId || this.getCompanyId();
    return this.http.get<TransporterFleetOption[]>(`${this.apiUrl}/client/transporters/scoped`, {
      params: new HttpParams().set('companyId', String(id))
    });
  }

  private appendRoleFilters(params: HttpParams, roles?: string[]): HttpParams {
    let next = params;
    for (const role of roles || []) {
      const normalizedRole = String(role || '').trim();
      if (normalizedRole) {
        next = next.append('roles', normalizedRole);
      }
    }
    return next;
  }

  // Leads
  getLeads(companyId?: number, roles?: string[]): Observable<CrmLead[]> {
    const id = companyId || this.getCompanyId();
    let params = new HttpParams().set('companyId', String(id));
    params = this.appendRoleFilters(params, roles);
    return this.http.get<CrmLead[]>(`${this.apiUrl}/lead/by-company`, { params });
  }

  getLead(id: number): Observable<APIResponseWrapper<CrmLead>> {
    return this.http.get<APIResponseWrapper<CrmLead>>(`${this.apiUrl}/lead/${id}`);
  }

  searchLeads(params: { companyId?: number; searchText?: string; status?: string; source?: string; leadNumber?: string; companyName?: string; pocName?: string; pocEmail?: string; pocPhoneNumber?: string; leadQuality?: string; roles?: string[]; page?: number; size?: number; sortBy?: string; sortDir?: string; }): Observable<PageResponse<CrmLead>> {
    const companyId = params.companyId || this.getCompanyId();
    let query = new HttpParams()
      .set('companyId', String(companyId))
      .set('searchText', params.searchText || '')
      .set('status', params.status || '')
      .set('source', params.source || '')
      .set('leadNumber', params.leadNumber || '')
      .set('companyName', params.companyName || '')
      .set('pocName', params.pocName || '')
      .set('pocEmail', params.pocEmail || '')
      .set('pocPhoneNumber', params.pocPhoneNumber || '')
      .set('leadQuality', params.leadQuality || '')
      .set('page', String(params.page ?? 0))
      .set('size', String(params.size ?? 20))
      .set('sortBy', params.sortBy || 'createdAt')
      .set('sortDir', params.sortDir || 'desc');
    query = this.appendRoleFilters(query, params.roles);
    return this.http.get<PageResponse<CrmLead>>(`${this.apiUrl}/lead/search`, { params: query });
  }

  addLead(lead: CrmLead): Observable<APIResponseWrapper<CrmLead>> {
    return this.http.post<APIResponseWrapper<CrmLead>>(`${this.apiUrl}/lead`, lead);
  }

  updateLead(lead: CrmLead): Observable<APIResponseWrapper<CrmLead>> {
    return this.http.put<APIResponseWrapper<CrmLead>>(`${this.apiUrl}/lead`, lead);
  }

  deleteLead(id: number): Observable<APIResponseWrapper<void>> {
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/lead/${id}`);
  }

  getLeadDuplicates(companyId?: number): Observable<APIResponseWrapper<CrmDuplicateResponse<CrmLead>>> {
    const id = companyId || this.getCompanyId();
    return this.http.get<APIResponseWrapper<CrmDuplicateResponse<CrmLead>>>(`${this.apiUrl}/lead/duplicates?companyId=${id}`);
  }

  getLeadScorecards(companyId?: number): Observable<CrmScorecards> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmScorecards>(`${this.apiUrl}/lead/scorecards?companyId=${id}`);
  }

  mergeLead(primaryId: number, duplicateId: number, companyId?: number): Observable<APIResponseWrapper<CrmLead>> {
    const id = companyId || this.getCompanyId();
    return this.http.post<APIResponseWrapper<CrmLead>>(`${this.apiUrl}/lead/${primaryId}/merge/${duplicateId}?companyId=${id}`, {});
  }

  convertLeadToClient(leadId: number, request: CrmLeadConversionRequest, companyId?: number): Observable<APIResponseWrapper<CrmLeadConversionResult>> {
    const id = companyId || this.getCompanyId();
    return this.http.post<APIResponseWrapper<CrmLeadConversionResult>>(`${this.apiUrl}/lead/${leadId}/convert?companyId=${id}`, request);
  }

  convertLeadToProspect(leadId: number, userId?: number, companyId?: number): Observable<APIResponseWrapper<CrmProspect>> {
    const id = companyId || this.getCompanyId();
    const userParam = userId ? `&userId=${userId}` : '';
    return this.http.post<APIResponseWrapper<CrmProspect>>(`${this.apiUrl}/lead/${leadId}/convert-to-prospect?companyId=${id}${userParam}`, {});
  }

  // Prospects
  getProspects(companyId?: number, roles?: string[]): Observable<CrmProspect[]> {
    const id = companyId || this.getCompanyId();
    let params = new HttpParams().set('companyId', String(id));
    params = this.appendRoleFilters(params, roles);
    return this.http.get<CrmProspect[]>(`${this.apiUrl}/prospect/by-company`, { params });
  }

  getProspect(id: number): Observable<APIResponseWrapper<CrmProspect>> {
    return this.http.get<APIResponseWrapper<CrmProspect>>(`${this.apiUrl}/prospect/${id}`);
  }

  searchProspects(params: { companyId?: number; searchText?: string; status?: string; source?: string; companyName?: string; pocName?: string; pocEmail?: string; pocPhoneNumber?: string; prospectQuality?: string; roles?: string[]; page?: number; size?: number; sortBy?: string; sortDir?: string; }): Observable<PageResponse<CrmProspect>> {
    const companyId = params.companyId || this.getCompanyId();
    let query = new HttpParams()
      .set('companyId', String(companyId))
      .set('searchText', params.searchText || '')
      .set('status', params.status || '')
      .set('source', params.source || '')
      .set('companyName', params.companyName || '')
      .set('pocName', params.pocName || '')
      .set('pocEmail', params.pocEmail || '')
      .set('pocPhoneNumber', params.pocPhoneNumber || '')
      .set('prospectQuality', params.prospectQuality || '')
      .set('page', String(params.page ?? 0))
      .set('size', String(params.size ?? 20))
      .set('sortBy', params.sortBy || 'createdAt')
      .set('sortDir', params.sortDir || 'desc');
    query = this.appendRoleFilters(query, params.roles);
    return this.http.get<PageResponse<CrmProspect>>(`${this.apiUrl}/prospect/search`, { params: query });
  }

  addProspect(prospect: CrmProspect): Observable<APIResponseWrapper<CrmProspect>> {
    return this.http.post<APIResponseWrapper<CrmProspect>>(`${this.apiUrl}/prospect`, prospect);
  }

  updateProspect(prospect: CrmProspect): Observable<APIResponseWrapper<CrmProspect>> {
    return this.http.put<APIResponseWrapper<CrmProspect>>(`${this.apiUrl}/prospect`, prospect);
  }

  deleteProspect(id: number): Observable<APIResponseWrapper<void>> {
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/prospect/${id}`);
  }

  convertProspectToClient(prospectId: number, request: CrmLeadConversionRequest, companyId?: number): Observable<APIResponseWrapper<CrmProspectConversionResult>> {
    const id = companyId || this.getCompanyId();
    return this.http.post<APIResponseWrapper<CrmProspectConversionResult>>(`${this.apiUrl}/prospect/${prospectId}/convert-to-client?companyId=${id}`, request);
  }

  getProspectScorecards(companyId?: number): Observable<CrmScorecards> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmScorecards>(`${this.apiUrl}/prospect/scorecards?companyId=${id}`);
  }

  // Clients
  getClients(companyId?: number, roles?: string[]): Observable<CrmClient[]> {
    const id = companyId || this.getCompanyId();
    let params = new HttpParams().set('companyId', String(id));
    params = this.appendRoleFilters(params, roles);
    return this.http.get<CrmClient[]>(`${this.apiUrl}/client/by-company`, { params });
  }

  searchClients(params: { companyId?: number; searchText?: string; category?: string; source?: string; clientNumber?: string; companyName?: string; website?: string; businessType?: string; primaryEmail?: string; phoneNumber?: string; approvalStatus?: string; roles?: string[]; page?: number; size?: number; sortBy?: string; sortDir?: string; }): Observable<PageResponse<CrmClient>> {
    const companyId = params.companyId || this.getCompanyId();
    let query = new HttpParams()
      .set('companyId', String(companyId))
      .set('searchText', params.searchText || '')
      .set('category', params.category || '')
      .set('source', params.source || '')
      .set('clientNumber', params.clientNumber || '')
      .set('companyName', params.companyName || '')
      .set('website', params.website || '')
      .set('businessType', params.businessType || '')
      .set('primaryEmail', params.primaryEmail || '')
      .set('phoneNumber', params.phoneNumber || '')
      .set('approvalStatus', params.approvalStatus || '')
      .set('page', String(params.page ?? 0))
      .set('size', String(params.size ?? 20))
      .set('sortBy', params.sortBy || 'createdAt')
      .set('sortDir', params.sortDir || 'desc');
    query = this.appendRoleFilters(query, params.roles);
    return this.http.get<PageResponse<CrmClient>>(`${this.apiUrl}/client/search`, { params: query });
  }

  addClient(client: CrmClient): Observable<APIResponseWrapper<CrmClient>> {
    return this.http.post<APIResponseWrapper<CrmClient>>(`${this.apiUrl}/client`, client);
  }

  quickCreateClient(request: CrmClientQuickCreateRequest): Observable<APIResponseWrapper<CrmClient>> {
    return this.http.post<APIResponseWrapper<CrmClient>>(`${this.apiUrl}/client/quick-create`, request);
  }

  quickCreateContact(clientId: number, companyId: number, request: CrmClientQuickContactRequest): Observable<APIResponseWrapper<CrmClientCommunication>> {
    const params = new HttpParams().set('companyId', String(companyId));
    return this.http.post<APIResponseWrapper<CrmClientCommunication>>(`${this.apiUrl}/client/${clientId}/quick-contacts`, request, { params });
  }

  updateClient(client: CrmClient): Observable<APIResponseWrapper<CrmClient>> {
    return this.http.put<APIResponseWrapper<CrmClient>>(`${this.apiUrl}/client`, client);
  }

  approveClient(id: number): Observable<APIResponseWrapper<CrmClient>> {
    return this.http.post<APIResponseWrapper<CrmClient>>(`${this.apiUrl}/client/${id}/approve`, {});
  }

  deleteClient(id: number): Observable<APIResponseWrapper<void>> {
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/client/${id}`);
  }

  getClient(id: number): Observable<APIResponseWrapper<CrmClient>> {
    return this.http.get<APIResponseWrapper<CrmClient>>(`${this.apiUrl}/client/${id}`);
  }

  getClientCreditControl(clientId: number, companyId?: number): Observable<CrmClientCreditSnapshot> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmClientCreditSnapshot>(`${this.apiUrl}/client/${clientId}/credit-control?companyId=${id}`);
  }

  getClientDuplicates(companyId?: number): Observable<APIResponseWrapper<CrmDuplicateResponse<CrmClient>>> {
    const id = companyId || this.getCompanyId();
    return this.http.get<APIResponseWrapper<CrmDuplicateResponse<CrmClient>>>(`${this.apiUrl}/client/duplicates?companyId=${id}`);
  }

  mergeClient(primaryId: number, duplicateId: number, companyId?: number): Observable<APIResponseWrapper<CrmClient>> {
    const id = companyId || this.getCompanyId();
    return this.http.post<APIResponseWrapper<CrmClient>>(`${this.apiUrl}/client/${primaryId}/merge/${duplicateId}?companyId=${id}`, {});
  }

  getClientScorecards(companyId?: number): Observable<CrmScorecards> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmScorecards>(`${this.apiUrl}/client/scorecards?companyId=${id}`);
  }

  getClientAddress(clientId: number): Observable<CrmClientAddress | null> {
    return this.http.get<CrmClientAddress | null>(`${this.apiUrl}/client/${clientId}/address`);
  }

  saveClientAddress(clientId: number, address: CrmClientAddress, companyId?: number): Observable<APIResponseWrapper<CrmClientAddress>> {
    const id = companyId || this.getCompanyId();
    return this.http.put<APIResponseWrapper<CrmClientAddress>>(`${this.apiUrl}/client/${clientId}/address?companyId=${id}`, address);
  }

  getClientMasterCommunications(clientId: number, companyId?: number): Observable<CrmClientCommunication[]> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmClientCommunication[]>(`${this.apiUrl}/client/${clientId}/communications?companyId=${id}`);
  }

  getClientContactDirectory(clientId: number, companyId?: number, accountId?: number | null): Observable<CrmClientContactDirectoryEntry[]> {
    let params = new HttpParams().set('companyId', String(companyId || this.getCompanyId()));
    if (accountId) params = params.set('accountId', String(accountId));
    return this.http.get<CrmClientContactDirectoryEntry[]>(`${this.apiUrl}/client/${clientId}/contact-directory`, { params });
  }

  resolveClientContactDirectoryEntry(clientId: number, request: CrmClientContactResolveRequest, companyId?: number): Observable<APIResponseWrapper<CrmClientCommunication>> {
    const params = new HttpParams().set('companyId', String(companyId || this.getCompanyId()));
    return this.http.post<APIResponseWrapper<CrmClientCommunication>>(`${this.apiUrl}/client/${clientId}/contact-directory/resolve`, request, { params });
  }

  saveClientMasterCommunication(clientId: number, communication: CrmClientCommunication, companyId?: number): Observable<APIResponseWrapper<CrmClientCommunication>> {
    const id = companyId || this.getCompanyId();
    if (communication.id) {
      return this.http.put<APIResponseWrapper<CrmClientCommunication>>(`${this.apiUrl}/client/${clientId}/communications/${communication.id}?companyId=${id}`, communication);
    }
    return this.http.post<APIResponseWrapper<CrmClientCommunication>>(`${this.apiUrl}/client/${clientId}/communications?companyId=${id}`, communication);
  }

  deleteClientMasterCommunication(clientId: number, communicationId: number, companyId?: number): Observable<APIResponseWrapper<void>> {
    const id = companyId || this.getCompanyId();
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/client/${clientId}/communications/${communicationId}?companyId=${id}`);
  }

  getClientDocuments(clientId: number, companyId?: number): Observable<CrmClientDocument[]> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmClientDocument[]>(`${this.apiUrl}/client/${clientId}/documents?companyId=${id}`);
  }

  saveClientDocument(clientId: number, document: CrmClientDocument, companyId?: number): Observable<APIResponseWrapper<CrmClientDocument>> {
    const id = companyId || this.getCompanyId();
    if (document.id) {
      return this.http.put<APIResponseWrapper<CrmClientDocument>>(`${this.apiUrl}/client/${clientId}/documents/${document.id}?companyId=${id}`, document);
    }
    return this.http.post<APIResponseWrapper<CrmClientDocument>>(`${this.apiUrl}/client/${clientId}/documents?companyId=${id}`, document);
  }

  deleteClientDocument(clientId: number, documentId: number, companyId?: number): Observable<APIResponseWrapper<void>> {
    const id = companyId || this.getCompanyId();
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/client/${clientId}/documents/${documentId}?companyId=${id}`);
  }

  // Accounts
  getAccounts(companyId?: number, crmClientId?: number): Observable<CrmAccount[]> {
    const id = companyId || this.getCompanyId();
    const query = new URLSearchParams({ companyId: String(id) });
    if (crmClientId !== undefined && crmClientId !== null) {
      query.set('crmClientId', String(crmClientId));
    }
    return this.http.get<CrmAccount[]>(`${this.apiUrl}/account/by-company?${query.toString()}`);
  }

  searchAccounts(params: { companyId?: number; crmClientId?: number; searchText?: string; category?: string; source?: string; page?: number; size?: number; sortBy?: string; sortDir?: string; }): Observable<PageResponse<CrmAccount>> {
    const companyId = params.companyId || this.getCompanyId();
    const query = new URLSearchParams({
      companyId: String(companyId),
      searchText: params.searchText || '',
      category: params.category || '',
      source: params.source || '',
      page: String(params.page ?? 0),
      size: String(params.size ?? 20),
      sortBy: params.sortBy || 'createdAt',
      sortDir: params.sortDir || 'desc'
    });
    if (params.crmClientId !== undefined && params.crmClientId !== null) {
      query.set('crmClientId', String(params.crmClientId));
    }
    return this.http.get<PageResponse<CrmAccount>>(`${this.apiUrl}/account/search?${query.toString()}`);
  }

  addAccount(account: CrmAccount): Observable<APIResponseWrapper<CrmAccount>> {
    return this.http.post<APIResponseWrapper<CrmAccount>>(`${this.apiUrl}/account`, account);
  }

  updateAccount(account: CrmAccount): Observable<APIResponseWrapper<CrmAccount>> {
    return this.http.put<APIResponseWrapper<CrmAccount>>(`${this.apiUrl}/account`, account);
  }

  deleteAccount(id: number): Observable<APIResponseWrapper<void>> {
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/account/${id}`);
  }

  getMerchandisers(companyId?: number): Observable<CrmMerchandiser[]> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmMerchandiser[]>(`${this.apiUrl}/merchandiser/by-company?companyId=${id}`);
  }

  searchMerchandisers(params: { companyId?: number; searchText?: string; active?: boolean | null; page?: number; size?: number; sortBy?: string; sortDir?: string; }): Observable<PageResponse<CrmMerchandiser>> {
    const companyId = params.companyId || this.getCompanyId();
    const query = new URLSearchParams({
      companyId: String(companyId),
      searchText: params.searchText || '',
      page: String(params.page ?? 0),
      size: String(params.size ?? 20),
      sortBy: params.sortBy || 'createdAt',
      sortDir: params.sortDir || 'desc'
    });
    if (params.active !== undefined && params.active !== null) {
      query.set('active', String(params.active));
    }
    return this.http.get<PageResponse<CrmMerchandiser>>(`${this.apiUrl}/merchandiser/search?${query.toString()}`);
  }

  getMerchandiser(id: number): Observable<APIResponseWrapper<CrmMerchandiser>> {
    return this.http.get<APIResponseWrapper<CrmMerchandiser>>(`${this.apiUrl}/merchandiser/${id}`);
  }

  addMerchandiser(merchandiser: CrmMerchandiser): Observable<APIResponseWrapper<CrmMerchandiser>> {
    return this.http.post<APIResponseWrapper<CrmMerchandiser>>(`${this.apiUrl}/merchandiser`, merchandiser);
  }

  updateMerchandiser(merchandiser: CrmMerchandiser): Observable<APIResponseWrapper<CrmMerchandiser>> {
    return this.http.put<APIResponseWrapper<CrmMerchandiser>>(`${this.apiUrl}/merchandiser`, merchandiser);
  }

  deleteMerchandiser(id: number): Observable<APIResponseWrapper<void>> {
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/merchandiser/${id}`);
  }

  getClientAssignments(companyId?: number): Observable<CrmClientEmployeeAssignment[]> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmClientEmployeeAssignment[]>(`${this.apiUrl}/client-assignment/by-company?companyId=${id}`);
  }

  getClientAssignmentsByClient(clientPartyId: number, companyId?: number): Observable<CrmClientEmployeeAssignment[]> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmClientEmployeeAssignment[]>(`${this.apiUrl}/client-assignment/by-client?companyId=${id}&clientPartyId=${clientPartyId}`);
  }

  getClientAssignmentsByEmployee(employeePartyId: number, companyId?: number): Observable<CrmClientEmployeeAssignment[]> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmClientEmployeeAssignment[]>(`${this.apiUrl}/client-assignment/by-employee?companyId=${id}&employeePartyId=${employeePartyId}`);
  }

  searchClientAssignments(params: { companyId?: number; searchText?: string; clientPartyId?: number; employeePartyId?: number; primaryOwner?: boolean | null; active?: boolean | null; page?: number; size?: number; sortBy?: string; sortDir?: string; }): Observable<PageResponse<CrmClientEmployeeAssignment>> {
    const companyId = params.companyId || this.getCompanyId();
    const query = new URLSearchParams({
      companyId: String(companyId),
      searchText: params.searchText || '',
      page: String(params.page ?? 0),
      size: String(params.size ?? 20),
      sortBy: params.sortBy || 'createdAt',
      sortDir: params.sortDir || 'desc'
    });
    if (params.clientPartyId) query.set('clientPartyId', String(params.clientPartyId));
    if (params.employeePartyId) query.set('employeePartyId', String(params.employeePartyId));
    if (params.primaryOwner !== undefined && params.primaryOwner !== null) query.set('primaryOwner', String(params.primaryOwner));
    if (params.active !== undefined && params.active !== null) query.set('active', String(params.active));
    return this.http.get<PageResponse<CrmClientEmployeeAssignment>>(`${this.apiUrl}/client-assignment/search?${query.toString()}`);
  }

  saveClientAssignments(request: CrmClientEmployeeAssignmentRequest): Observable<APIResponseWrapper<CrmClientEmployeeAssignment[]>> {
    return this.http.post<APIResponseWrapper<CrmClientEmployeeAssignment[]>>(`${this.apiUrl}/client-assignment`, request);
  }

  deleteClientAssignment(id: number): Observable<APIResponseWrapper<void>> {
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/client-assignment/${id}`);
  }

  getAssignmentScorecards(companyId?: number): Observable<CrmScorecards> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmScorecards>(`${this.apiUrl}/client-assignment/scorecards?companyId=${id}`);
  }

  getClientRegistrationAttachments(clientId: number, companyId?: number): Observable<CrmClientRegistrationAttachment[]> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmClientRegistrationAttachment[]>(`${this.apiUrl}/client/${clientId}/registration-attachments?companyId=${id}`);
  }

  saveClientRegistrationAttachment(clientId: number, attachment: CrmClientRegistrationAttachment, companyId?: number): Observable<APIResponseWrapper<CrmClientRegistrationAttachment>> {
    const id = companyId || this.getCompanyId();
    return this.http.post<APIResponseWrapper<CrmClientRegistrationAttachment>>(`${this.apiUrl}/client/${clientId}/registration-attachments?companyId=${id}`, attachment);
  }

  deleteClientRegistrationAttachment(clientId: number, attachmentId: number, companyId?: number): Observable<APIResponseWrapper<void>> {
    const id = companyId || this.getCompanyId();
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/client/${clientId}/registration-attachments/${attachmentId}?companyId=${id}`);
  }

  getLeadRegistrationAttachments(leadId: number, companyId?: number): Observable<CrmClientRegistrationAttachment[]> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmClientRegistrationAttachment[]>(`${this.apiUrl}/lead/${leadId}/registration-attachments?companyId=${id}`);
  }

  saveLeadRegistrationAttachment(leadId: number, attachment: CrmClientRegistrationAttachment, companyId?: number): Observable<APIResponseWrapper<CrmClientRegistrationAttachment>> {
    const id = companyId || this.getCompanyId();
    return this.http.post<APIResponseWrapper<CrmClientRegistrationAttachment>>(`${this.apiUrl}/lead/${leadId}/registration-attachments?companyId=${id}`, attachment);
  }

  deleteLeadRegistrationAttachment(leadId: number, attachmentId: number, companyId?: number): Observable<APIResponseWrapper<void>> {
    const id = companyId || this.getCompanyId();
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/lead/${leadId}/registration-attachments/${attachmentId}?companyId=${id}`);
  }

  getProspectRegistrationAttachments(prospectId: number, companyId?: number): Observable<CrmClientRegistrationAttachment[]> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmClientRegistrationAttachment[]>(`${this.apiUrl}/prospect/${prospectId}/registration-attachments?companyId=${id}`);
  }

  saveProspectRegistrationAttachment(prospectId: number, attachment: CrmClientRegistrationAttachment, companyId?: number): Observable<APIResponseWrapper<CrmClientRegistrationAttachment>> {
    const id = companyId || this.getCompanyId();
    return this.http.post<APIResponseWrapper<CrmClientRegistrationAttachment>>(`${this.apiUrl}/prospect/${prospectId}/registration-attachments?companyId=${id}`, attachment);
  }

  deleteProspectRegistrationAttachment(prospectId: number, attachmentId: number, companyId?: number): Observable<APIResponseWrapper<void>> {
    const id = companyId || this.getCompanyId();
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/prospect/${prospectId}/registration-attachments/${attachmentId}?companyId=${id}`);
  }

  // Quotations
  getQuotations(): Observable<APIResponseWrapper<CrmQuotation[]>> {
    const companyId = this.getCompanyId();
    return this.http.get<CrmQuotation[]>(`${this.apiUrl}/quotation/by-company?companyId=${companyId}`)
      .pipe(map(data => ({ success: true, data } as APIResponseWrapper<CrmQuotation[]>)));
  }

  searchQuotations(params: { companyId?: number; searchText?: string; status?: string; type?: string; jobType?: string; deviationStatus?: string; leadId?: number; prospectId?: number; clientId?: number; kamCustomerId?: number; startFrom?: Date | string; startTo?: Date | string; amountFrom?: number; amountTo?: number; page?: number; size?: number; sortBy?: string; sortDir?: string; }): Observable<PageResponse<CrmQuotation>> {
    const companyId = params.companyId || this.getCompanyId();
    const query = new URLSearchParams({
      companyId: String(companyId),
      searchText: params.searchText || '',
      status: params.status || '',
      type: params.type || '',
      jobType: params.jobType || '',
      deviationStatus: params.deviationStatus || '',
      leadId: params.leadId ? String(params.leadId) : '',
      prospectId: params.prospectId ? String(params.prospectId) : '',
      clientId: params.clientId ? String(params.clientId) : '',
      kamCustomerId: params.kamCustomerId ? String(params.kamCustomerId) : '',
      startFrom: params.startFrom ? this.formatDateParam(params.startFrom) : '',
      startTo: params.startTo ? this.formatDateParam(params.startTo) : '',
      amountFrom: params.amountFrom != null ? String(params.amountFrom) : '',
      amountTo: params.amountTo != null ? String(params.amountTo) : '',
      page: String(params.page ?? 0),
      size: String(params.size ?? 20),
      sortBy: params.sortBy || 'createdAt',
      sortDir: params.sortDir || 'desc'
    });
    return this.http.get<PageResponse<CrmQuotation>>(`${this.apiUrl}/quotation/search?${query.toString()}`);
  }

  getQuotationScorecards(companyId?: number): Observable<CrmScorecards> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmScorecards>(`${this.apiUrl}/quotation/scorecards?companyId=${id}`);
  }

  getQuotation(id: number): Observable<APIResponseWrapper<CrmQuotation>> {
    const companyId = this.getCompanyId();
    return this.http.get<APIResponseWrapper<CrmQuotation>>(`${this.apiUrl}/quotation/${id}?companyId=${companyId}`);
  }

  saveQuotation(quotation: CrmQuotation): Observable<APIResponseWrapper<CrmQuotation>> {
    if (!quotation.companyId) {
      quotation.companyId = this.getCompanyId();
    }
    return this.http.post<APIResponseWrapper<CrmQuotation>>(`${this.apiUrl}/quotation`, quotation);
  }

  saveQuotationLines(id: number, lines: CrmQuotation['lines']): Observable<APIResponseWrapper<CrmQuotation>> {
    const companyId = this.getCompanyId();
    return this.http.put<APIResponseWrapper<CrmQuotation>>(`${this.apiUrl}/quotation/${id}/lines?companyId=${companyId}`, lines || []);
  }

  deleteQuotation(id: number): Observable<APIResponseWrapper<void>> {
    const companyId = this.getCompanyId();
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/quotation/${id}?companyId=${companyId}`);
  }

  convertToProject(id: number): Observable<APIResponseWrapper<PosmProject>> {
    const companyId = this.getCompanyId();
    return this.http.post<APIResponseWrapper<PosmProject>>(`${this.apiUrl}/quotation/${id}/convert?companyId=${companyId}`, {});
  }

  previewContractConversion(id: number, request: QuoteToContractRequest): Observable<APIResponseWrapper<QuoteToContractPreview>> {
    const companyId = this.getCompanyId();
    return this.http.post<APIResponseWrapper<QuoteToContractPreview>>(`${this.apiUrl}/quotation/${id}/contract-conversion/preview?companyId=${companyId}`, request || {});
  }

  convertQuotationToContract(id: number, request: QuoteToContractRequest): Observable<APIResponseWrapper<QuoteToContractResult>> {
    const companyId = this.getCompanyId();
    return this.http.post<APIResponseWrapper<QuoteToContractResult>>(`${this.apiUrl}/quotation/${id}/contract-conversion?companyId=${companyId}`, request);
  }

  getQuotationContractLinks(id: number): Observable<APIResponseWrapper<QuotationContractLink[]>> {
    const companyId = this.getCompanyId();
    return this.http.get<APIResponseWrapper<QuotationContractLink[]>>(`${this.apiUrl}/quotation/${id}/contract-links?companyId=${companyId}`);
  }

  // Quotation Actions
  approveQuotation(id: number, userId: number): Observable<APIResponseWrapper<CrmQuotation>> {
    const companyId = this.getCompanyId();
    return this.http.post<APIResponseWrapper<CrmQuotation>>(`${this.apiUrl}/quotation/${id}/approve?userId=${userId}&companyId=${companyId}`, {});
  }

  rejectQuotation(id: number, userId: number, reason: string): Observable<APIResponseWrapper<CrmQuotation>> {
    const companyId = this.getCompanyId();
    return this.http.post<APIResponseWrapper<CrmQuotation>>(`${this.apiUrl}/quotation/${id}/reject?userId=${userId}&reason=${reason}&companyId=${companyId}`, {});
  }

  sendQuotationEmail(id: number): Observable<APIResponseWrapper<void>> {
    const companyId = this.getCompanyId();
    return this.http.post<APIResponseWrapper<void>>(`${this.apiUrl}/quotation/${id}/email?companyId=${companyId}`, {});
  }

  transitionQuotation(id: number, actionCode: string, comment?: string): Observable<APIResponseWrapper<CrmQuotation>> {
    const companyId = this.getCompanyId();
    const performedByCustomerId = this.toolbarService.customer()?.id;
    return this.http.post<APIResponseWrapper<CrmQuotation>>(`${this.apiUrl}/quotation/${id}/transition?companyId=${companyId}`, {
      actionCode,
      comment,
      performedByCustomerId
    });
  }

  submitQuotation(id: number, recipientEmail: string, expiresAt?: Date, comment?: string): Observable<APIResponseWrapper<CrmQuotationExternalReview>> {
    const companyId = this.getCompanyId();
    return this.http.post<APIResponseWrapper<CrmQuotationExternalReview>>(`${this.apiUrl}/quotation/${id}/submit?companyId=${companyId}`, {
      recipientEmail,
      expiresAt,
      comment,
      submittedByCustomerId: this.toolbarService.customer()?.id
    });
  }

  getQuotationAttachments(id: number): Observable<APIResponseWrapper<CrmQuotationAttachment[]>> {
    const companyId = this.getCompanyId();
    return this.http.get<APIResponseWrapper<CrmQuotationAttachment[]>>(`${this.apiUrl}/quotation/${id}/attachments?companyId=${companyId}`);
  }

  saveQuotationAttachment(id: number, attachment: CrmQuotationAttachment): Observable<APIResponseWrapper<CrmQuotationAttachment>> {
    const companyId = this.getCompanyId();
    attachment.uploadedByCustomerId = this.toolbarService.customer()?.id ?? attachment.uploadedByCustomerId;
    return this.http.post<APIResponseWrapper<CrmQuotationAttachment>>(`${this.apiUrl}/quotation/${id}/attachments?companyId=${companyId}`, attachment);
  }

  deleteQuotationAttachment(id: number, attachmentId: number): Observable<APIResponseWrapper<void>> {
    const companyId = this.getCompanyId();
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/quotation/${id}/attachments/${attachmentId}?companyId=${companyId}`);
  }

  getLatestQuotationPdf(id: number): Observable<APIResponseWrapper<CrmQuotationAttachment>> {
    const companyId = this.getCompanyId();
    return this.http.get<APIResponseWrapper<CrmQuotationAttachment>>(`${this.apiUrl}/quotation/${id}/pdf/latest?companyId=${companyId}`);
  }

  generateQuotationPdf(id: number, regenerate = false): Observable<APIResponseWrapper<CrmQuotationAttachment>> {
    const companyId = this.getCompanyId();
    const generatedByCustomerId = this.toolbarService.customer()?.id;
    let url = `${this.apiUrl}/quotation/${id}/pdf/generate?companyId=${companyId}&regenerate=${regenerate}`;
    if (generatedByCustomerId) {
      url += `&generatedByCustomerId=${generatedByCustomerId}`;
    }
    return this.http.post<APIResponseWrapper<CrmQuotationAttachment>>(url, {});
  }

  previewCfExecutionFromQuotation(id: number, request: any = {}, companyId?: number): Observable<any> {
    const owningCompanyId = companyId || this.getCompanyId();
    return this.http.post<any>(
      `${environment.restApiUrl}cf-executions/from-quotation/${id}/preview?owningCompanyId=${owningCompanyId}`,
      request || {}
    );
  }

  createCfExecutionFromQuotation(id: number, request: any = {}, companyId?: number): Observable<any> {
    const owningCompanyId = companyId || this.getCompanyId();
    return this.http.post<any>(
      `${environment.restApiUrl}cf-executions/from-quotation/${id}?owningCompanyId=${owningCompanyId}`,
      request || {}
    );
  }

  getQuotationRequirements(id: number): Observable<APIResponseWrapper<CrmQuotationRequirement[]>> {
    const companyId = this.getCompanyId();
    return this.http.get<APIResponseWrapper<CrmQuotationRequirement[]>>(`${this.apiUrl}/quotation/${id}/requirements?companyId=${companyId}`);
  }

  saveQuotationRequirement(id: number, requirement: CrmQuotationRequirement): Observable<APIResponseWrapper<CrmQuotationRequirement>> {
    const companyId = this.getCompanyId();
    const customerId = this.toolbarService.customer()?.id;
    requirement.createdByCustomerId = requirement.id ? requirement.createdByCustomerId : customerId;
    requirement.updatedByCustomerId = requirement.id ? customerId : requirement.updatedByCustomerId;
    return this.http.post<APIResponseWrapper<CrmQuotationRequirement>>(`${this.apiUrl}/quotation/${id}/requirements?companyId=${companyId}`, requirement);
  }

  getQuotationTasks(id: number): Observable<APIResponseWrapper<CrmQuotationTask[]>> {
    const companyId = this.getCompanyId();
    return this.http.get<APIResponseWrapper<CrmQuotationTask[]>>(`${this.apiUrl}/quotation/${id}/tasks?companyId=${companyId}`);
  }

  saveQuotationTask(id: number, task: CrmQuotationTask): Observable<APIResponseWrapper<CrmQuotationTask>> {
    const companyId = this.getCompanyId();
    const customerId = this.toolbarService.customer()?.id;
    task.createdByCustomerId = task.id ? task.createdByCustomerId : customerId;
    task.updatedByCustomerId = task.id ? customerId : task.updatedByCustomerId;
    return this.http.post<APIResponseWrapper<CrmQuotationTask>>(`${this.apiUrl}/quotation/${id}/tasks?companyId=${companyId}`, task);
  }

  getQuotationReviewNotes(id: number): Observable<APIResponseWrapper<CrmQuotationReviewNote[]>> {
    const companyId = this.getCompanyId();
    return this.http.get<APIResponseWrapper<CrmQuotationReviewNote[]>>(`${this.apiUrl}/quotation/${id}/review-notes?companyId=${companyId}`);
  }

  saveQuotationReviewNote(id: number, note: CrmQuotationReviewNote): Observable<APIResponseWrapper<CrmQuotationReviewNote>> {
    const companyId = this.getCompanyId();
    note.createdByCustomerId = this.toolbarService.customer()?.id ?? note.createdByCustomerId;
    return this.http.post<APIResponseWrapper<CrmQuotationReviewNote>>(`${this.apiUrl}/quotation/${id}/review-notes?companyId=${companyId}`, note);
  }

  getQuotationExternalReviews(id: number): Observable<APIResponseWrapper<CrmQuotationExternalReview[]>> {
    const companyId = this.getCompanyId();
    return this.http.get<APIResponseWrapper<CrmQuotationExternalReview[]>>(`${this.apiUrl}/quotation/${id}/external-reviews?companyId=${companyId}`);
  }

  getQuotationTermsTemplates(companyId?: number): Observable<CrmQuotationTermsTemplate[]> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmQuotationTermsTemplate[]>(`${this.apiUrl}/quotation/terms-templates?companyId=${id}`);
  }

  saveQuotationTermsTemplate(template: CrmQuotationTermsTemplate): Observable<APIResponseWrapper<CrmQuotationTermsTemplate>> {
    if (!template.companyId) template.companyId = this.getCompanyId();
    if (template.id) {
      return this.http.put<APIResponseWrapper<CrmQuotationTermsTemplate>>(`${this.apiUrl}/quotation/terms-templates/${template.id}`, template);
    }
    return this.http.post<APIResponseWrapper<CrmQuotationTermsTemplate>>(`${this.apiUrl}/quotation/terms-templates`, template);
  }

  deleteQuotationTermsTemplate(id: number): Observable<APIResponseWrapper<void>> {
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/quotation/terms-templates/${id}`);
  }

  getQuotationClientCriteria(companyId?: number): Observable<CrmQuotationClientCriteria[]> {
    const id = companyId || this.getCompanyId();
    return this.http.get<CrmQuotationClientCriteria[]>(`${this.apiUrl}/quotation/client-criteria?companyId=${id}`);
  }

  saveQuotationClientCriteria(criteria: CrmQuotationClientCriteria): Observable<APIResponseWrapper<CrmQuotationClientCriteria>> {
    if (!criteria.companyId) criteria.companyId = this.getCompanyId();
    if (criteria.id) {
      return this.http.put<APIResponseWrapper<CrmQuotationClientCriteria>>(`${this.apiUrl}/quotation/client-criteria/${criteria.id}`, criteria);
    }
    return this.http.post<APIResponseWrapper<CrmQuotationClientCriteria>>(`${this.apiUrl}/quotation/client-criteria`, criteria);
  }

  deleteQuotationClientCriteria(id: number): Observable<APIResponseWrapper<void>> {
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/quotation/client-criteria/${id}`);
  }

  // Notes
  getNotesByLead(leadId: number): Observable<CrmNote[]> {
    return this.http.get<CrmNote[]>(`${this.apiUrl}/note/by-lead?leadId=${leadId}&companyId=${this.getCompanyId()}`);
  }

  getNotesByProspect(prospectId: number): Observable<CrmNote[]> {
    return this.http.get<CrmNote[]>(`${this.apiUrl}/note/by-prospect?prospectId=${prospectId}&companyId=${this.getCompanyId()}`);
  }

  getNotesByClient(clientId: number): Observable<CrmNote[]> {
    return this.http.get<CrmNote[]>(`${this.apiUrl}/note/by-client?clientId=${clientId}&companyId=${this.getCompanyId()}`);
  }

  saveNote(note: CrmNote): Observable<APIResponseWrapper<CrmNote>> {
    if (!note.companyId) note.companyId = this.getCompanyId();
    return this.http.post<APIResponseWrapper<CrmNote>>(`${this.apiUrl}/note`, note);
  }

  deleteNote(id: number): Observable<APIResponseWrapper<void>> {
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/note/${id}?companyId=${this.getCompanyId()}`);
  }

  // Communications
  getCommunicationLogsByLead(leadId: number): Observable<CrmCommunicationLog[]> {
    return this.http.get<CrmCommunicationLog[]>(`${this.apiUrl}/communication/by-lead?leadId=${leadId}&companyId=${this.getCompanyId()}`);
  }

  getCommunicationLogsByProspect(prospectId: number): Observable<CrmCommunicationLog[]> {
    return this.http.get<CrmCommunicationLog[]>(`${this.apiUrl}/communication/by-prospect?prospectId=${prospectId}&companyId=${this.getCompanyId()}`);
  }

  getCommunicationLogsByClient(clientId: number): Observable<CrmCommunicationLog[]> {
    return this.http.get<CrmCommunicationLog[]>(`${this.apiUrl}/communication/by-client?clientId=${clientId}&companyId=${this.getCompanyId()}`);
  }

  saveCommunicationLog(log: CrmCommunicationLog): Observable<APIResponseWrapper<CrmCommunicationLog>> {
    if (!log.companyId) log.companyId = this.getCompanyId();
    return this.http.post<APIResponseWrapper<CrmCommunicationLog>>(`${this.apiUrl}/communication`, log);
  }

  deleteCommunicationLog(id: number): Observable<APIResponseWrapper<void>> {
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/communication/${id}?companyId=${this.getCompanyId()}`);
  }

  // Reminders
  getRemindersByUser(userId: number): Observable<CrmReminder[]> {
    return this.http.get<CrmReminder[]>(`${this.apiUrl}/reminder/by-user?assignedUserId=${userId}&companyId=${this.getCompanyId()}`);
  }

  getRemindersByLead(leadId: number): Observable<CrmReminder[]> {
    return this.http.get<CrmReminder[]>(`${this.apiUrl}/reminder/by-lead?leadId=${leadId}&companyId=${this.getCompanyId()}`);
  }

  getRemindersByProspect(prospectId: number): Observable<CrmReminder[]> {
    return this.http.get<CrmReminder[]>(`${this.apiUrl}/reminder/by-prospect?prospectId=${prospectId}&companyId=${this.getCompanyId()}`);
  }

  getRemindersByClient(clientId: number): Observable<CrmReminder[]> {
    return this.http.get<CrmReminder[]>(`${this.apiUrl}/reminder/by-client?clientId=${clientId}&companyId=${this.getCompanyId()}`);
  }

  saveReminder(reminder: CrmReminder): Observable<APIResponseWrapper<CrmReminder>> {
    if (!reminder.companyId) reminder.companyId = this.getCompanyId();
    return this.http.post<APIResponseWrapper<CrmReminder>>(`${this.apiUrl}/reminder`, reminder);
  }

  deleteReminder(id: number): Observable<APIResponseWrapper<void>> {
    return this.http.delete<APIResponseWrapper<void>>(`${this.apiUrl}/reminder/${id}?companyId=${this.getCompanyId()}`);
  }

  getLeadActivityTimeline(leadId: number): Observable<CrmActivityItem[]> {
    return this.http.get<CrmActivityItem[]>(`${this.apiUrl}/activity/by-lead?leadId=${leadId}&companyId=${this.getCompanyId()}`);
  }

  getProspectActivityTimeline(prospectId: number): Observable<CrmActivityItem[]> {
    return this.http.get<CrmActivityItem[]>(`${this.apiUrl}/activity/by-prospect?prospectId=${prospectId}&companyId=${this.getCompanyId()}`);
  }

  getClientActivityTimeline(clientId: number): Observable<CrmActivityItem[]> {
    return this.http.get<CrmActivityItem[]>(`${this.apiUrl}/activity/by-client?clientId=${clientId}&companyId=${this.getCompanyId()}`);
  }

  getAnalyticsSummary(): Observable<APIResponseWrapper<CrmAnalyticsSummary>> {
    return this.http.get<APIResponseWrapper<CrmAnalyticsSummary>>(`${this.apiUrl}/analytics/summary?companyId=${this.getCompanyId()}`);
  }

  private formatDateParam(value: Date | string): string {
    if (value instanceof Date) {
      return value.toISOString().slice(0, 10);
    }
    return value;
  }
}
